import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { formatCredits, formatDate, formatDuration, formatUsd } from "@/lib/api";

function StatusPill({ status, callSuccessful }) {
  const primary = callSuccessful || status || "unknown";
  const map = {
    success: { text: "SUCCESS", color: "text-[#16A34A] border-[#16A34A]/40 bg-[#16A34A]/5" },
    failure: { text: "FAILURE", color: "text-[#DC2626] border-[#DC2626]/40 bg-[#DC2626]/5" },
    done: { text: "DONE", color: "text-black/80 border-black/25" },
    "in-progress": { text: "IN-PROGRESS", color: "text-[#CA8A04] border-[#CA8A04]/40 bg-[#CA8A04]/5" },
    processing: { text: "PROCESSING", color: "text-[#CA8A04] border-[#CA8A04]/40 bg-[#CA8A04]/5" },
    failed: { text: "FAILED", color: "text-[#DC2626] border-[#DC2626]/40 bg-[#DC2626]/5" },
    unknown: { text: "UNKNOWN", color: "text-black/45 border-black/20" },
  };
  const conf = map[primary] || map.unknown;
  return (
    <span className={`inline-flex items-center border px-2 py-0.5 font-mono-plex text-[9px] uppercase tracking-[0.15em] ${conf.color}`}>
      {conf.text}
    </span>
  );
}

function TitleCell({ row }) {
  const primary = row.call_summary_title || row.title || row.external_id;
  const sub = [row.agent_name, row.main_language, row.conversation_initiation_source]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="flex flex-col max-w-[380px] min-w-0">
      <span className="text-sm text-black truncate" title={primary}>
        {primary}
      </span>
      {sub && (
        <span className="font-mono-plex text-[10px] text-black/45 truncate" title={sub}>
          {sub}
        </span>
      )}
    </div>
  );
}

function MobileCard({ row, selected, toggleSelect, onClick }) {
  const primary = row.call_summary_title || row.title || row.external_id;
  const sub = [row.agent_name, row.main_language].filter(Boolean).join(" · ");
  return (
    <div
      data-testid={`mobile-card-${row.external_id}`}
      className="border-b border-black/10 p-4 active:bg-black/[0.04] cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <div onClick={(e) => e.stopPropagation()} className="pt-1">
          <Checkbox
            data-testid={`select-${row.external_id}`}
            checked={selected}
            onCheckedChange={() => toggleSelect(row.external_id)}
            className="border-black/30 data-[state=checked]:bg-black data-[state=checked]:text-white rounded-none"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-base text-black truncate font-semibold leading-tight" title={primary}>
                {primary}
              </div>
              {sub && (
                <div className="font-mono-plex text-xs text-black/55 truncate mt-1">
                  {sub}
                </div>
              )}
            </div>
            <StatusPill status={row.status} callSuccessful={row.call_successful} />
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-4">
            {[
              ["DATE", formatDate(row.date)],
              ["DURATION", formatDuration(row.duration)],
              ["MESSAGES", row.messages_count ?? 0],
              ["TOTAL COST", formatUsd(row.total_cost_usd)],
              ["LLM COST", formatUsd(row.llm_cost_usd)],
            ].map(([label, value]) => (
              <div key={label} className="flex flex-col min-w-0">
                <span className="font-mono-plex text-[10px] uppercase tracking-[0.15em] text-black/40">
                  {label}
                </span>
                <span className="font-mono-plex text-sm text-black/85 truncate mt-0.5" title={String(value)}>
                  {value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ConversationTable({
  rows,
  loading,
  total,
  page,
  pageSize,
  setPage,
  onRowClick,
  selectedIds,
  toggleSelect,
  toggleSelectAll,
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.external_id));

  return (
    <div className="flex flex-col min-h-full bg-white">
      {/* Mobile card list */}
      <div className="md:hidden flex-1 overflow-auto" data-testid="mobile-list">
        {loading && rows.length === 0 ? (
          <div className="py-16 text-center">
            <Loader2 className="w-5 h-5 animate-spin inline-block text-black/40" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16">
            <div className="flex flex-col items-center gap-3" data-testid="empty-state-mobile">
              <div className="w-12 h-12 border border-black/15 grid place-items-center">
                <div className="w-2 h-2 bg-black/30" />
              </div>
              <p className="font-heading text-lg text-black/70">No conversations</p>
              <p className="font-mono-plex text-[11px] text-black/45 uppercase tracking-[0.15em] text-center px-6">
                Connect your API key and hit Sync Now
              </p>
            </div>
          </div>
        ) : (
          rows.map((r) => (
            <MobileCard
              key={r.external_id}
              row={r}
              selected={selectedIds.has(r.external_id)}
              toggleSelect={toggleSelect}
              onClick={() => onRowClick(r)}
            />
          ))
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block flex-1 overflow-auto">
        <table className="w-full border-collapse font-body" data-testid="conversation-table">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="border-b border-black/15">
              <th className="w-10 py-3 pl-4 text-left">
                <Checkbox
                  data-testid="select-all-checkbox"
                  checked={allSelected}
                  onCheckedChange={toggleSelectAll}
                  className="border-black/30 data-[state=checked]:bg-black data-[state=checked]:text-white rounded-none"
                />
              </th>
              {[
                "Title",
                "Date",
                "Duration",
                "Msgs",
                "Credits",
                "LLM $",
                "Total $",
                "Source",
                "Status",
              ].map((h) => (
                <th
                  key={h}
                  className="text-left py-3 px-3 font-mono-plex text-[10px] font-bold uppercase tracking-[0.2em] text-black/55 whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="py-24 text-center">
                  <Loader2 className="w-5 h-5 animate-spin inline-block text-black/40" />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="py-24">
                  <div className="flex flex-col items-center gap-3" data-testid="empty-state">
                    <div className="w-12 h-12 border border-black/15 grid place-items-center">
                      <div className="w-2 h-2 bg-black/30" />
                    </div>
                    <p className="font-heading text-lg text-black/70">No conversations yet</p>
                    <p className="font-mono-plex text-[11px] text-black/45 uppercase tracking-[0.15em]">
                      Connect your API key and hit Sync Now
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr
                  key={r.external_id}
                  data-testid={`conversation-row-${r.external_id}`}
                  className="border-b border-black/5 row-hover hover:bg-black/[0.03] cursor-pointer"
                  onClick={() => onRowClick(r)}
                >
                  <td className="py-3 pl-4" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      data-testid={`select-desktop-${r.external_id}`}
                      checked={selectedIds.has(r.external_id)}
                      onCheckedChange={() => toggleSelect(r.external_id)}
                      className="border-black/30 data-[state=checked]:bg-black data-[state=checked]:text-white rounded-none"
                    />
                  </td>
                  <td className="py-3 px-3">
                    <TitleCell row={r} />
                  </td>
                  <td className="py-3 px-3 font-mono-plex text-xs text-black/80 whitespace-nowrap">
                    {formatDate(r.date)}
                  </td>
                  <td className="py-3 px-3 font-mono-plex text-xs text-black/80 whitespace-nowrap">
                    {formatDuration(r.duration)}
                  </td>
                  <td className="py-3 px-3 font-mono-plex text-xs text-black/80">
                    {r.messages_count ?? 0}
                  </td>
                  <td className="py-3 px-3 font-mono-plex text-xs text-black/80 whitespace-nowrap">
                    {formatCredits(r.credits_used ?? r.conversation_cost)}
                  </td>
                  <td className="py-3 px-3 font-mono-plex text-xs text-black/80 whitespace-nowrap">
                    {formatUsd(r.llm_cost_usd)}
                  </td>
                  <td className="py-3 px-3 font-mono-plex text-xs text-black/80 whitespace-nowrap">
                    {formatUsd(r.total_cost_usd)}
                  </td>
                  <td className="py-3 px-3 font-mono-plex text-[10px] uppercase tracking-[0.15em] text-black/65 whitespace-nowrap">
                    {r.conversation_initiation_source || "—"}
                  </td>
                  <td className="py-3 px-3">
                    <StatusPill status={r.status} callSuccessful={r.call_successful} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className="flex items-center justify-between px-3 md:px-4 py-3 border-t border-black/10 bg-white">
        <div
          className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/55"
          data-testid="pagination-info"
        >
          <span className="hidden sm:inline">Showing </span>
          {rows.length ? (page - 1) * pageSize + 1 : 0}–{(page - 1) * pageSize + rows.length}
          <span className="hidden sm:inline"> of </span>
          <span className="sm:hidden"> / </span>
          {total}
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="prev-page-button"
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            className="h-8 rounded-none border-black/15 bg-white text-black hover:bg-black/5 hover:text-black disabled:opacity-40"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </Button>
          <span className="font-mono-plex text-xs text-black/75 min-w-[60px] text-center">
            {page} / {totalPages}
          </span>
          <Button
            data-testid="next-page-button"
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
            className="h-8 rounded-none border-black/15 bg-white text-black hover:bg-black/5 hover:text-black disabled:opacity-40"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
